import React from "react";
import {
  Card,
  Box,
  Stack,
  Typography,
  Avatar,
  Container,
  Divider,
  Paper,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import Email from "@mui/icons-material/Email";
import SchoolIcon from "@mui/icons-material/School";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import CollectionIcon from "@mui/icons-material/Collections";
import MailIcon from "@mui/icons-material/Mail";
import ImageIcon from "@mui/icons-material/Image";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useUserProfile, useMyGroups, useMyCollections, useMyPendingInvites, useUserImageCount } from "../hooks/useQueries";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2979ff", // A nice blue color
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

export const Profile = () => {
  const { user } = useAuth0();
  const navigate = useNavigate();

  const { data: profileData } = useUserProfile();
  const { data: groups = [] } = useMyGroups();
  const { data: collections = [] } = useMyCollections();
  const { data: invites = [] } = useMyPendingInvites();
  const { data: imageCount = 0 } = useUserImageCount(user?.email || '');

  const userData = profileData || {
    affiliation: "",
    email: user?.email || "",
    name: user?.name || "",
    subId: user?.sub || "",
    givenName: user?.givenName || "",
  };

  const stats = {
    groupCount: groups.length,
    collectionCount: collections.length,
    inviteCount: invites.length,
    imageCount,
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxShadow: 3,
            borderRadius: 2,
          }}
        >
          <Avatar
            sx={{ width: 80, height: 80, bgcolor: "primary.main", mb: 2 }}
          >
            <PersonIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {userData.name}
          </Typography>
          <Divider sx={{ width: "100%", my: 2 }} />
          <Stack spacing={2} sx={{ width: "100%" }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Email sx={{ color: grey[500] }} />
              <Typography variant="body1" color="text.secondary">
                {userData.email}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SchoolIcon sx={{ color: grey[500] }} />
              <Typography variant="body1" color="text.secondary">
                {userData.affiliation || "No Affiliation"}
              </Typography>
            </Stack>
          </Stack>
        </Card>

        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Paper
              sx={{
                p: 2.5,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: 4,
                  transform: "translateY(-2px)",
                },
              }}
              onClick={() => navigate("/groups")}
            >
              <GroupIcon sx={{ fontSize: 40, color: "#2979ff", mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {stats.groupCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Groups
              </Typography>
            </Paper>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Paper
              sx={{
                p: 2.5,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: 4,
                  transform: "translateY(-2px)",
                },
              }}
              onClick={() => navigate("/collections")}
            >
              <CollectionIcon sx={{ fontSize: 40, color: "#00897b", mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {stats.collectionCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Collections
              </Typography>
            </Paper>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Paper
              sx={{
                p: 2.5,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: 4,
                  transform: "translateY(-2px)",
                },
              }}
              onClick={() => navigate("/invites")}
            >
              <MailIcon sx={{ fontSize: 40, color: "#f57c00", mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {stats.inviteCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Invites
              </Typography>
            </Paper>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Paper
              sx={{
                p: 2.5,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: 4,
                  transform: "translateY(-2px)",
                },
              }}
              onClick={() => navigate("/images")}
            >
              <ImageIcon sx={{ fontSize: 40, color: "#7b1fa2", mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {stats.imageCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Images
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default Profile;
